// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import React from 'react';
import { Box, Flex, Text, WarningIcon } from '~/design-system';
import { bottlebooksFieldsSource } from '~/pages/imports/productAllInOneImport/[importId]/_config';
import Notification from '../Notification';
import FieldsTable from './FieldsTable';

/**
 * Renders the page where fields are mapped
 * All columns from import are rendered and Bottlebooks fields are available in the dropdown
 * @param {Object} props
 * @param {boolean} props.loading - true if data is loading
 * @param {Object[]} [props.latestBottlebooksFields] - the previous fields mapped to be used on new imports
 * @param {Notification} [props.notification]
 * @param {function} props.cancelNotification
 * @param {object} props.bottlebooksFieldsMap
 * @param {object[]} props.bottlebooksFieldsSource
 * @param {{name:string, sampleRows: string[]}[]} props.sourceFields
 * @param {function} props.handleFieldValueTableChange
 * @param {function} props.handleFieldValueTableSkipToggle
 */
export default function MapValues({
  loading,
  notification,
  cancelNotification,
  sourceFields,
  handleFieldValueTableChange,
  handleFieldValueTableSkipToggle,
  ...rest
}) {
  if (loading || !sourceFields) return null;

  return (
    <React.Fragment>
      <UnmatchedValuesSummary sourceFields={sourceFields} />
      <FieldsTable
        sx={{ margin: '0 auto', marginBottom: 5, marginTop: 2 }}
        sourceFields={sourceFields}
        onChange={handleFieldValueTableChange}
        onToggleSkip={handleFieldValueTableSkipToggle}
        {...rest}
      />

      {notification && (
        <Notification
          onClose={() => cancelNotification()}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

/**
 * Lists the fields with values that are not matched to a valid value yet.
 * These are what keeps the Next button disabled.
 * @param {object} props
 * @param {object[]} props.sourceFields
 */
function UnmatchedValuesSummary({ sourceFields }) {
  const unmatchedFields = getUnmatchedFields(sourceFields);
  if (!unmatchedFields.length) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'brand.warningYellow',
        backgroundColor: 'brand.lightGray',
        borderRadius: 4,
        padding: 3,
        marginTop: 2,
        marginBottom: 3,
      }}
    >
      <Flex sx={{ alignItems: 'center', marginBottom: 2 }}>
        <WarningIcon
          sx={{
            color: 'brand.warningYellow',
            width: '20px',
            height: '20px',
            marginRight: 2,
          }}
        />
        <Text sx={{ fontWeight: 'bold' }}>
          <Trans>
            Match the following values (or skip them) to continue to the next
            step:
          </Trans>
        </Text>
      </Flex>
      {unmatchedFields.map(({ bottlebooksFieldName, label, rawTexts }) => (
        <Text key={bottlebooksFieldName} sx={{ marginLeft: 4 }}>
          {label}: {rawTexts.join(', ')}
        </Text>
      ))}
    </Box>
  );
}

/**
 * The fields with values that fail the areAllValuesFilled check, with the
 * distinct raw texts that are not matched.
 * @param {object[]} sourceFields
 */
function getUnmatchedFields(sourceFields) {
  if (!sourceFields) return [];
  return sourceFields.reduce((acc, sourceField) => {
    const validKeys = sourceField.validValues.map(({ key }) => key);
    const rawTexts = [];
    sourceField.values?.forEach((oneValue) => {
      if (oneValue.isValueMappedToNull) return;
      const cleanedRawText =
        typeof oneValue.rawText === 'string'
          ? oneValue.rawText.trim()
          : oneValue.rawText;
      if (!cleanedRawText) return;
      if (validKeys.includes(oneValue.value)) return;
      if (!rawTexts.includes(cleanedRawText)) rawTexts.push(cleanedRawText);
    });
    if (rawTexts.length) {
      const label =
        bottlebooksFieldsSource?.find(
          ({ key }) => key === sourceField.bottlebooksFieldName
        )?.label || sourceField.name;
      acc.push({
        bottlebooksFieldName: sourceField.bottlebooksFieldName,
        label,
        rawTexts,
      });
    }
    return acc;
  }, []);
}

export function useMapValues({ workspaceId, importId }) {
  const { loading, /* error, */ data /*, refetch */ } = useQuery(QUERY, {
    variables: {
      workspaceId,
      importId,
    },
  });

  // Source fields state is modified by the user and only saved at the end.
  // We start with the state returned by the GraphQL whenever the page is
  // loaded.
  const { sourceFields: initialSourceFields } = afterQuery({ data });
  const [currentSourceFields, setCurrentSourceFields] = React.useState();
  // If the state has been modified, use current state
  const sourceFields = currentSourceFields || initialSourceFields;

  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  /** @type {[boolean, (boolean)=>void]} */
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [_updateColumnsMapping] = useMutation(UPDATE_VALUES_MAPPING);

  async function updateValuesMapping(__sourceFields) {
    const _sourceFields = __sourceFields || sourceFields;
    if (!_sourceFields || isSubmitting) return;
    const valueMappingsMap = _sourceFields?.reduce((fieldsMap, sourceField) => {
      sourceField.values?.forEach((fieldValue) => {
        if (fieldValue.isValueMappedToNull) {
          console.log('fieldValue.isValueMappedToNull', fieldValue);
        }
        if (fieldValue.isDirty || fieldValue.isValueMappedToNull) {
          const uniqString = `${sourceField.bottlebooksFieldName}_${fieldValue.rawText}`;
          if (!fieldsMap[uniqString]) {
            fieldsMap[uniqString] = {
              bottlebooksFieldName: sourceField.bottlebooksFieldName,
              importedFieldValue: fieldValue.rawText,
              bottlebooksFieldValue: fieldValue.isValueMappedToNull
                ? 'isValueMappedToNull'
                : fieldValue.value,
              isValueMappedToNull: fieldValue.isValueMappedToNull,
            };
          }
        }
      });
      return fieldsMap;
    }, {});
    const valueMappings = Object.values(valueMappingsMap);
    if (!valueMappings?.length) return; //No data to update
    try {
      setIsSubmitting(true);
      // TODO There duplicates in valueMappings we should filter them out
      const { data } = await _updateColumnsMapping({
        variables: {
          input: {
            importId,
            valueMappings,
          },
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmitting(false);
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsSubmitting(false);
      return { error: err };
    }
  }

  /**
   * Used to check if all the values are mapped
   * @param {object[]} sourceFields
   * @returns boolean
   */
  function areAllValuesFilled(sourceFields) {
    if (!sourceFields) return false;
    const allValid = sourceFields.reduce((isValid, sourceField) => {
      const validKeys = sourceField.validValues.map(({ key }) => key);
      const oneFieldValid = sourceField.values.reduce(
        (valuesValid, oneValue) => {
          if (oneValue.isValueMappedToNull) return valuesValid;
          const cleanedRawText =
            typeof oneValue.rawText === 'string'
              ? oneValue.rawText.trim()
              : oneValue.rawText;
          const oneValueValid = cleanedRawText
            ? validKeys.includes(oneValue.value)
            : true; //fields without any raw text defined should be ignored.
          return valuesValid && oneValueValid;
        },
        true
      );
      return isValid && oneFieldValid;
    }, true);

    return allValid;
  }

  async function handleFieldValueTableChange(data) {
    const { option, sourceField, sourceFields } = data;
    const newSourceFields = sourceFields?.map((oneSourceField) => {
      if (
        sourceField.bottlebooksFieldName === oneSourceField.bottlebooksFieldName
      ) {
        const newValues = oneSourceField.values?.reduce((acc, oneValue) => {
          if (sameTrimmedRawText(sourceField.rawText, oneValue.rawText)) {
            const value = option && option.key ? option.key : undefined;
            acc.push({
              ...oneValue,
              value,
              isDirty: true,
              isSuggestion: false,
            });
          } else {
            acc.push(oneValue);
          }
          return acc;
        }, []);

        return { ...oneSourceField, values: newValues };
      }
      return oneSourceField;
    });
    setCurrentSourceFields(newSourceFields);
  }
  async function handleFieldValueTableSkipToggle(data) {
    const { option, sourceField, sourceFields } = data;
    const newSourceFields = sourceFields?.map((oneSourceField) => {
      if (
        sourceField.bottlebooksFieldName === oneSourceField.bottlebooksFieldName
      ) {
        // console.log('oneSourceField', oneSourceField);
        const newValues = oneSourceField.values?.reduce((acc, oneValue) => {
          if (sameTrimmedRawText(sourceField.rawText, oneValue.rawText)) {
            // const value = option && option.key ? option.key : undefined;
            acc.push({
              ...oneValue,
              // value: 'isValueMappedToNull',
              isValueMappedToNull: !oneValue.isValueMappedToNull,
              isDirty: true,
              isSuggestion: false,
            });
          } else {
            acc.push(oneValue);
          }
          return acc;
        }, []);
        return { ...oneSourceField, values: newValues };
      }
      return oneSourceField;
    });
    setCurrentSourceFields(newSourceFields);
    await updateValuesMapping(newSourceFields);
  }

  // multiple fields are merged into one here.
  const sourceFieldsMerged = sourceFields?.reduce((merged, sourceField) => {
    if (sourceField.bottlebooksFieldName?.includes('.0.')) {
      const fieldName = sourceField.bottlebooksFieldName;
      const existing = merged.find(
        ({ bottlebooksFieldName }) => bottlebooksFieldName === fieldName
      );
      if (existing) {
        existing.values = [...existing.values, ...sourceField.values];
        existing.names.push(sourceField.name);
      } else {
        merged.push({
          ...sourceField,
          bottlebooksFieldName: fieldName,
          names: [sourceField.name],
          isMultiple: true,
        });
      }
    } else {
      merged.push(sourceField);
    }

    return merged;
  }, []);

  const sourceFieldsWithValues = [];
  const sourceFieldsWithoutAnyValues = [];
  sourceFieldsMerged?.forEach((sourceField) => {
    const sourceFieldUniqueValues = getUniqueSourceFieldValues({
      sourceField,
    });
    const enhanced = { ...sourceField, sourceFieldUniqueValues };
    if (sourceFieldUniqueValues) sourceFieldsWithValues.push(enhanced);
    else sourceFieldsWithoutAnyValues.push(enhanced);
  });

  return {
    loading,
    importId,
    // All fields that have valid values lists
    // Free-text fields are excluded
    sourceFields,
    // Fields that have values in source data
    sourceFieldsWithValues,
    // Fields that don't have any values at all in source data
    sourceFieldsWithoutAnyValues,
    updateValuesMapping,
    isValid: areAllValuesFilled(sourceFields) && !isSubmitting,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    handleFieldValueTableChange,
    handleFieldValueTableSkipToggle,
  };
}

/**
 * Used to get the distinct values imported for the current source field.
 * @param {object} props
 * @param {object} props.sourceField - the current source field.
 * @returns
 */
function getUniqueSourceFieldValues({ sourceField }) {
  const uniqueValuesMap = sourceField?.values?.reduce(
    (uniqueValues, oneValue) => {
      const cleaned =
        typeof oneValue.rawText === 'string'
          ? oneValue.rawText.trim()
          : oneValue.rawText;
      if (cleaned) uniqueValues[cleaned] = oneValue;
      return uniqueValues;
    },
    {}
  );

  const uniqueValues = Object.values(uniqueValuesMap);
  if (!uniqueValues?.length) return null;
  return uniqueValues;
}


/**
 * Raw texts that differ only by surrounding whitespace are the same value to
 * the user — the table dedupes rows by trimmed text.
 * @param {any} a
 * @param {any} b
 */
function sameTrimmedRawText(a, b) {
  if (typeof a === 'string' && typeof b === 'string')
    return a.trim() === b.trim();
  return a === b;
}

function afterQuery({ data }) {
  if (!data) return {};
  // We just render fields that have valid values
  const sourceFields = data?.workspace?.dataImport?.source?.fields
    .filter(({ validValues }) => validValues)
    .map((sourceField) => {
      return {
        ...sourceField,
        values: propagateAcrossWhitespaceVariants(
          sourceField.values.map((value) => {
          // When no value is saved we use from suggestion when it's 100% probability
          const suggestion = value.suggestedValues?.find(
            ({ probability }) => probability === 1
          );
          if (!value.value && suggestion) {
            return {
              ...value,
              value: suggestion.value,
              isDirty: true,
              isSuggestion: true, //so we know that value was not set by the user
            };
          }
          // Fall back to a near-unique substring match on the valid value
          // keys (e.g. raw "Elgin" -> key "Elgin Valley"). This used to be
          // display-only in the Match dropdown, which showed a match that
          // validation never saw — the field looked mapped but blocked Next.
          const substringMatch = findSubstringMatch({
            rawText: value.rawText,
            value: value.value,
            suggestedValues: value.suggestedValues,
            validValues: sourceField.validValues,
          });
          if (substringMatch) {
            return {
              ...value,
              value: substringMatch,
              isDirty: true,
              isSuggestion: true,
            };
          }
          return value;
        })
        ),
      };
    });
  return {
    sourceFields,
  };
}

/**
 * Cells that differ only by surrounding whitespace (raw "Elgin" vs "Elgin ")
 * render as one row (the table dedupes by trimmed text), so a mapping saved
 * for one variant must apply to all of them — otherwise the unmapped variant
 * invisibly blocks the Next button.
 * @param {object[]} values
 */
function propagateAcrossWhitespaceVariants(values) {
  const mappedByTrimmedText = {};
  values.forEach((value) => {
    if (typeof value.rawText !== 'string') return;
    const trimmed = value.rawText.trim();
    if (!trimmed || mappedByTrimmedText[trimmed]) return;
    if (value.value || value.isValueMappedToNull)
      mappedByTrimmedText[trimmed] = value;
  });
  return values.map((value) => {
    if (value.value || value.isValueMappedToNull) return value;
    if (typeof value.rawText !== 'string') return value;
    const mapped = mappedByTrimmedText[value.rawText.trim()];
    if (!mapped) return value;
    return {
      ...value,
      value: mapped.value,
      isValueMappedToNull: mapped.isValueMappedToNull,
      isDirty: true,
      isSuggestion: true,
    };
  });
}

/**
 * The valid value key whose text contains the raw text, when there are at
 * most 2 such keys (same rule the Match dropdown used to apply display-only).
 * Prefers the shortest matching key. Returns undefined when the value is
 * already set, a server suggestion exists, or the match is ambiguous.
 * @param {object} props
 * @param {string} props.rawText
 * @param {string} [props.value]
 * @param {object[]} [props.suggestedValues]
 * @param {{key: string}[]} [props.validValues]
 */
function findSubstringMatch({ rawText, value, suggestedValues, validValues }) {
  if (value || suggestedValues?.length) return undefined;
  if (!rawText || typeof rawText !== 'string') return undefined;
  // Padded cells (" Elgin ") must match the same keys their trimmed text does.
  const cleanedRawText = rawText.trim();
  if (!cleanedRawText) return undefined;
  let matches = 0;
  let bestKey = '';
  validValues?.forEach(({ key }) => {
    if (key.indexOf(cleanedRawText) > -1) {
      matches++;
      if (!bestKey || bestKey.length > key.length) bestKey = key;
    }
  });
  if (matches && matches < 3) return bestKey;
  return undefined;
}

const mapFieldsSourceFragment = gql`
  fragment MapValuesSource on DataImport {
    importId
    source {
      fields {
        name
        bottlebooksFieldName
        validValues(locale: en) {
          key
          label
        }
        values {
          bottlebooksFieldName
          name
          rawText
          value
          isValueMappedToNull
          suggestedValues {
            probability
            value
          }
        }
      }
    }
  }
`;
const QUERY = gql`
  query MapValuesPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      configuration {
        workspaceId
        customProductFields {
          label
          key
          type
        }
      }
      dataImport(importId: $importId) {
        importId
        ...MapValuesSource
      }
    }
  }
  ${mapFieldsSourceFragment}
`;

export const UPDATE_VALUES_MAPPING = gql`
  mutation updateDataImportValueMappings(
    $input: updateDataImportValueMappingsInput!
  ) {
    result: updateDataImportValueMappings(input: $input) {
      code
      success
      message
      dataImport {
        importId
        ...MapValuesSource
      }
    }
  }
  ${mapFieldsSourceFragment}
`;
